import React from 'react';
import { motion } from 'framer-motion';

const capabilitiesData = [
    {
        id: 1,
        title: "Inventory & Asset Management",
        image: "/assets/glodesk/img7.png",
        points: [
            "Real-time stock tracking with simple, clear insights",
            "Easy barcode and QR code scanning for faster processing",
            "Efficient batch handling to save time and reduce errors",
            "Organized Bill of Materials (BOM) management",
            "Smooth quality control workflows for better accuracy and consistency",
        ]
    },
    {
        id: 2,
        title: "Document & Data Automation",
        image: "/assets/glodesk/img8.png",
        points: [
            "Built-in OCR to quickly scan and extract data from documents",
            "Automated data entry to reduce manual work and errors",
            "Template-based processing for consistent and reliable outputs",
            "Seamless integration with orders and existing business workflows",
        ]
    },
    {
        id: 3,
        title: "Field & Service Operations",
        image: "/assets/glodesk/img9.png",
        points: [
            "Simple task tracking and checklists for everyday work",
            "Mobile-friendly design for on-site teams to work easily anywhere",
            "Easy service ticketing to manage and resolve issues quickly",
            "Smart system for handling renewals without missing deadlines",
            "Organized management of dealers, vendors, and suppliers in one place"
        ]
    },
    {
        id: 4,
        title: "Order & WorkFlow Management",
        image: "/assets/glodesk/img10.png",
        points: [
            "Track every order clearly from start to finish",
            "Visual pipeline for easy monitoring and approvals",
            "Smooth coordination between vendors, dealers, and fitters",
            "Simple workflow to reduce confusion and delays",
            "Better communication for faster, more reliable execution",
        ]
    },
    {
        id: 5,
        title: "Insights & Collaboration",
        image: "/assets/glodesk/img11.png",
        points: [
            "Secure role-based user access for better control",
            "In-app communication with real-time notifications",
            "Easy-to-use dashboards for finance, sales, and operations",
            "Clear insights that help teams make faster decisions",
            "Smooth and connected experience across all workflows",
        ]
    },
];

const CoreCapabilities = () => {
    const headerVariants = {
        hidden: { opacity: 0, y: -20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    return (
        <section className="w-full bg-white pb-[60px] md:pb-[100px] overflow-hidden">
            <div className="max-w-[1440px] mx-auto lg:px-[80px] px-[20px]">

                {/* Header Section */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={headerVariants}
                    className="text-center max-w-[564px] mx-auto mb-[48px] md:mb-[64px]"
                >
                    <h2 className="text-[20px] md:text-[30px] font-sora font-[600] text-[#01386A] tracking-tight mb-[12px] md:mb-[16px]">
                        Core Capabilities
                    </h2>
                    <p className="text-[#49577A] text-[14px] sm:text-[16px] font-graphik font-[500] leading-[1.6]">
                        We aim to make a real, positive difference for our customers, the industry we work in, and the communities we are part of.
                    </p>
                </motion.div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                    className="space-y-[32px]"
                >
                    {capabilitiesData.map((item) => (
                        <motion.div
                            key={item.id}
                            variants={cardVariants}
                            className="group relative flex flex-col md:flex-row items-stretch justify-between gap-[32px] p-[24px] md:p-[40px] bg-white border border-gray-200 rounded-sm transition-all duration-300 overflow-hidden cursor-pointer hover:shadow-[0_15px_40px_rgba(1,56,106,0.05)]"
                        >
                            {/* Left Content Side: Title at Top, List Points at Bottom */}
                            <div className="flex-1 flex flex-col justify-between space-y-[24px] w-full order-2 md:order-1">
                                <h3 className="text-[22px] md:text-[30px] font-sora font-[600] text-[#01386A] tracking-tight">
                                    {item.title}
                                </h3>
                                <ul className="space-y-[16px] max-w-[510px] w-full mt-auto">
                                    {item.points.map((point, index) => (
                                        <li key={index} className="flex items-start gap-[12px] text-[14px] sm:text-[16px] text-[#49577A] font-graphik font-[500] leading-[1.5]">
                                            <span className="text-[#ED6800] font-bold text-[16px] select-none mt-[-1px]">
                                                &gt;
                                            </span>
                                            <span>{point}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="w-full lg:w-full max-w-[529px] md:h-[240px] lg:h-[353px] flex-shrink-0 overflow-hidden order-1 md:order-2">
                                <img
                                    src={item.image}
                                    alt={item.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-102"
                                />
                            </div>
                            <div className="absolute bottom-0 left-0 h-[4px] w-full bg-[#01386A] z-10" />
                            <div className="absolute bottom-0 left-0 h-[4px] w-0 bg-[#ED6800] transition-all duration-500 ease-in-out group-hover:w-full z-20" />
                        </motion.div>
                    ))}
                </motion.div>

            </div>
        </section>
    );
};

export default CoreCapabilities;